"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.RoutineDiscardSummary = exports.CustodyOriginScope = void 0;
const zod_1 = require("zod");
const IsoDateTime = zod_1.z.string().datetime();
const custodyId = zod_1.z
    .string()
    .min(1)
    .max(128)
    .refine((v) => v.trim().length > 0, 'Blank custody identity');
const logicalBytes = zod_1.z.number().int().positive().max(Number.MAX_SAFE_INTEGER);
/** Original intake scope; never reconstructed from current placement. */
exports.CustodyOriginScope = zod_1.z
    .object({
    tenantId: zod_1.z.string().uuid(),
    storeId: custodyId,
})
    .strict();
/**
 * Protected append-only evidence of routine data intentionally discarded.
 * It does not acknowledge, replace, or discard protected evidence and must
 * never become a weighing, attendant vote, audit finding or savings claim.
 * originalTerminalId is Link-attested provenance when sourceComponent=link,
 * not an impersonated SCO principal. Bytes are logical persisted custody
 * bytes actually removed, including duplicate body/journal/outbox copies and
 * removed routine metadata. Summary/tombstone metadata remains protected.
 * These logical retained UTF-8 bytes are not allocated/free physical space.
 */
exports.RoutineDiscardSummary = zod_1.z
    .object({
    schemaVersion: zod_1.z.literal(1),
    clientUuid: zod_1.z.string().uuid(),
    recordType: zod_1.z.literal('routine_discard_summary'),
    sourceComponent: zod_1.z.enum(['sco', 'link']),
    emitterId: custodyId,
    originalScope: exports.CustodyOriginScope,
    originalTerminalId: custodyId,
    originalVersionIds: zod_1.z.array(custodyId).min(1).max(128),
    custodyUnit: zod_1.z.enum(['record', 'batch']),
    custodyIds: zod_1.z.array(custodyId).min(1).max(64),
    occurredAt: IsoDateTime,
    capturedFrom: IsoDateTime,
    capturedTo: IsoDateTime,
    receivedFrom: IsoDateTime,
    receivedTo: IsoDateTime,
    reason: zod_1.z.literal('storage_pressure'),
    discardedRoutineCount: zod_1.z.number().int().positive().max(Number.MAX_SAFE_INTEGER),
    discardedRoutineBytes: logicalBytes,
})
    .strict()
    .superRefine((record, ctx) => {
    if (Date.parse(record.capturedTo) < Date.parse(record.capturedFrom) ||
        Date.parse(record.receivedTo) < Date.parse(record.receivedFrom)) {
        ctx.addIssue({ code: zod_1.z.ZodIssueCode.custom, message: 'Reversed custody time bounds' });
    }
    if (new Set(record.custodyIds).size !== record.custodyIds.length ||
        new Set(record.originalVersionIds).size !== record.originalVersionIds.length) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            message: 'Duplicate custody or version identity',
        });
    }
    if (record.sourceComponent === 'sco' &&
        (record.custodyUnit !== 'record' ||
            record.emitterId !== record.originalTerminalId ||
            record.discardedRoutineCount !== record.custodyIds.length)) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            message: 'SCO summary must identify its own discarded records',
        });
    }
    if (record.sourceComponent === 'link' &&
        (record.custodyUnit !== 'batch' || record.discardedRoutineCount < record.custodyIds.length)) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            message: 'Link summary must preserve whole custody batch associations',
        });
    }
});
//# sourceMappingURL=custody-telemetry.js.map